import React, { useState } from "react";
import { motion } from "framer-motion";
import {
  Phone,
  Mail,
  MapPin,
  Clock,
  Send,
  User,
  MessageSquare,
  Building,
  Star,
  CheckCircle,
} from "lucide-react";
import CustomDropdown from "../components/CustomDropdown";
import { useTranslation } from "react-i18next";

export const ContactPage: React.FC = () => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    subject: "",
    message: "",
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const handleInputChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await fetch("/api/contact", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          name: `${formData.firstName} ${formData.lastName}`,
          email: formData.email,
          phone: formData.phone,
          date: "",
          time: "",
          passengers: "",
          pickup: "",
          destination: "",
          flightNumber: "",
          message: `Subject: ${formData.subject}\n\n${formData.message}`,
          source: "taxischipholairport", // Identify this site
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || t("1321", "Failed to send message"));
      }

      setIsSubmitting(false);
      setIsSuccess(true);

      // Reset form after success
      setTimeout(() => {
        setFormData({
          firstName: "",
          lastName: "",
          email: "",
          phone: "",
          subject: "",
          message: "",
        });
        setIsSuccess(false);
      }, 3000);
    } catch (error) {
      setIsSubmitting(false);
      console.error("Contact form error:", error);
      alert(t("1322", "Failed to send message. Please try again."));
    }
  };

  const contactMethods = [
    {
      icon: Phone,
      title: t("1323", "Have any question?"),
      subtitle: t("1324", "Call us directly"),
      value: "+31 6 39182624",
      link: "tel:+31639182624",
      gradient: "from-primary to-primary-dark",
    },
    {
      icon: Mail,
      title: t("1325", "Write email"),
      subtitle: t("1326", "Send us an email"),
      value: "info@taxischipholairport.net",
      link: "mailto:info@taxischipholairport.net",
      gradient: "from-accent to-primary-light",
    },
    {
      icon: MapPin,
      title: t("1327", "Visit anytime"),
      subtitle: t("1328", "Our office location"),
      value: "Stationsplein NO 264, 1046 AH Schiphol",
      link: "#map",
      gradient: "from-warning to-primary",
    },
  ];

  const subjectOptions = [
    { value: "General Inquiry", label: t("1329", "General Inquiry") },
    { value: "Booking Request", label: t("1330", "Booking Request") },
    { value: "Business Transfer", label: t("1331", "Business Transfer") },
    { value: "Airport Service", label: t("1332", "Airport Service") },
    { value: "City Transfer", label: t("1333", "City Transfer") },
    { value: "Complaint", label: t("1334", "Complaint") },
    { value: "Feedback", label: t("1335", "Feedback") },
    { value: "Other", label: t("1336", "Other") },
  ];

  return (
    <div className="pt-20 bg-gray-900 min-h-screen overflow-x-hidden">
      {/* Hero Section */}
      <section className="relative py-16 sm:py-20 lg:py-24 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="/images/background/bg2.webp"
            alt={t("1337", "Contact Us")}
            className="w-full h-full object-cover opacity-70"
          />
          <div className="absolute inset-0 bg-linear-to-r from-gray-900/90 to-gray-800/90" />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center text-white"
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6 }}
              className="inline-flex items-center space-x-2 bg-primary/20 text-primary px-6 py-3 rounded-full text-sm font-medium mb-6"
            >
              <MessageSquare className="h-4 w-4" />
              <span>{t("1338", "Get In Touch")}</span>
            </motion.div>

            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-7xl font-bold mb-6 sm:mb-8">
              {t("1339", "Contact")}{" "}
              <span className="bg-linear-to-r from-primary to-primary-light bg-clip-text text-transparent">
                TaxiSchipholAirport
              </span>
            </h1>

            <p className="text-lg sm:text-xl lg:text-2xl text-gray-200 max-w-4xl mx-auto leading-relaxed">
              {t(
                "1340",
                "We're here to help you with all your transportation needs. Reach out to us anytime!"
              )}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Contact Methods */}
      <section className="py-16 sm:py-20 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
              {t("1341", "Get In Touch With Us")}
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              {t("1342", "Multiple ways to reach us for your convenience")}
            </p>
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8 mb-12 sm:mb-16">
            {contactMethods.map((method, index) => (
              <motion.a
                key={method.title}
                href={method.link}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.2 }}
                whileHover={{ y: -5, scale: 1.02 }}
                className="group bg-gray-800/50 backdrop-blur-xs rounded-3xl p-6 sm:p-8 border border-gray-700 hover:border-primary/50 transition-all duration-300 text-center"
              >
                <div
                  className={`inline-flex items-center justify-center w-16 h-16 bg-linear-to-r ${method.gradient} rounded-2xl mb-6 group-hover:scale-110 transition-transform duration-300`}
                >
                  <method.icon className="h-8 w-8 text-white" />
                </div>

                <h3 className="text-2xl font-bold text-white mb-2 group-hover:text-primary transition-colors duration-300">
                  {method.title}
                </h3>

                <p className="text-gray-400 mb-4">{method.subtitle}</p>

                <p className="text-lg text-gray-200 font-semibold">
                  {method.value}
                </p>
              </motion.a>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Form & Business Info */}
      <section className="py-16 sm:py-20 lg:py-24 bg-gray-800/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-16">
            {/* Contact Form */}
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <div className="bg-gray-800/50 backdrop-blur-xs rounded-3xl p-6 sm:p-8 border border-gray-700">
                <h3 className="text-2xl sm:text-3xl font-bold text-white mb-6 sm:mb-8">
                  {t("1343", "Send Us a Message")}
                </h3>

                {isSuccess ? (
                  <div className="text-center py-8">
                    <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-100 mb-4">
                      <CheckCircle className="w-8 h-8 text-green-500" />
                    </div>
                    <h3 className="text-xl font-bold text-white mb-2">
                      {t("1344", "Message Sent Successfully!")}
                    </h3>
                    <p className="text-gray-300">
                      {t(
                        "1345",
                        "Thank you for contacting us. We'll get back to you soon."
                      )}
                    </p>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">
                          {t("1346", "First Name")} *
                        </label>
                        <div className="relative">
                          <User className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                          <input
                            type="text"
                            name="firstName"
                            value={formData.firstName}
                            onChange={handleInputChange}
                            required
                            className="w-full pl-10 pr-4 py-3 bg-gray-700/50 border border-gray-600 rounded-xl text-white placeholder-gray-400 focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all duration-300"
                            placeholder={t("1347", "John")}
                          />
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">
                          {t("1348", "Last Name")} *
                        </label>
                        <div className="relative">
                          <User className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                          <input
                            type="text"
                            name="lastName"
                            value={formData.lastName}
                            onChange={handleInputChange}
                            required
                            className="w-full pl-10 pr-4 py-3 bg-gray-700/50 border border-gray-600 rounded-xl text-white placeholder-gray-400 focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all duration-300"
                            placeholder={t("1349", "Doe")}
                          />
                        </div>
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">
                        {t("1350", "Email Address")} *
                      </label>
                      <div className="relative">
                        <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                        <input
                          type="email"
                          name="email"
                          value={formData.email}
                          onChange={handleInputChange}
                          required
                          className="w-full pl-10 pr-4 py-3 bg-gray-700/50 border border-gray-600 rounded-xl text-white placeholder-gray-400 focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all duration-300"
                          placeholder={t("1351", "john@example.com")}
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">
                        {t("1352", "Phone Number")}
                      </label>
                      <div className="relative">
                        <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                        <input
                          type="tel"
                          name="phone"
                          value={formData.phone}
                          onChange={handleInputChange}
                          className="w-full pl-10 pr-4 py-3 bg-gray-700/50 border border-gray-600 rounded-xl text-white placeholder-gray-400 focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all duration-300"
                          placeholder={t("1353", "+31 6 12345678")}
                        />
                      </div>
                    </div>

                    <div>
                      <CustomDropdown
                        options={subjectOptions}
                        value={formData.subject}
                        onChange={(value) =>
                          setFormData((prev) => ({ ...prev, subject: value }))
                        }
                        placeholder={t("1354", "Select a subject")}
                        label={t("1355", "Subject")}
                        required
                        icon={<MessageSquare size={18} />}
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">
                        {t("1356", "Message")} *
                      </label>
                      <textarea
                        name="message"
                        value={formData.message}
                        onChange={handleInputChange}
                        required
                        rows={6}
                        className="w-full px-4 py-3 bg-gray-700/50 border border-gray-600 rounded-xl text-white placeholder-gray-400 focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all duration-300 resize-none"
                        placeholder={t(
                          "1357",
                          "Tell us how we can help you..."
                        )}
                      />
                    </div>

                    <motion.button
                      type="submit"
                      disabled={isSubmitting}
                      whileHover={{ scale: isSubmitting ? 1 : 1.02 }}
                      whileTap={{ scale: isSubmitting ? 1 : 0.98 }}
                      className="w-full bg-linear-to-r from-primary to-primary-light hover:from-primary-dark hover:to-primary disabled:from-gray-600 disabled:to-gray-600 text-white px-8 py-4 rounded-xl font-semibold transition-all duration-300 flex items-center justify-center space-x-2 shadow-lg hover:shadow-xl disabled:cursor-not-allowed"
                    >
                      <Send className="h-5 w-5" />
                      <span>
                        {isSubmitting
                          ? t("1358", "Sending...")
                          : t("1359", "Send Message")}
                      </span>
                    </motion.button>
                  </form>
                )}
              </div>
            </motion.div>

            {/* Business Information */}
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="space-y-8"
            >
              {/* Company Info */}
              <div className="bg-gray-800/50 backdrop-blur-xs rounded-3xl p-6 sm:p-8 border border-gray-700">
                <div className="flex items-center space-x-3 mb-6">
                  <Building className="h-8 w-8 text-primary" />
                  <h3 className="text-2xl font-bold text-white">
                    {t("1360", "Company Information")}
                  </h3>
                </div>

                <div className="space-y-4">
                  <div className="flex items-start space-x-3">
                    <MapPin className="h-5 w-5 text-primary mt-1 shrink-0" />
                    <div>
                      <p className="font-semibold text-white">
                        {t("1361", "Address")}
                      </p>
                      <p className="text-gray-300">
                        Stationsplein NO 264, 1046 AH Schiphol
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start space-x-3">
                    <Building className="h-5 w-5 text-primary mt-1 shrink-0" />
                    <div>
                      <p className="font-semibold text-white">
                        {t("1362", "KVK Number")}
                      </p>
                      <p className="text-gray-300">69753938</p>
                    </div>
                  </div>

                  <div className="flex items-start space-x-3">
                    <Phone className="h-5 w-5 text-primary mt-1 shrink-0" />
                    <div>
                      <p className="font-semibold text-white">
                        {t("1363", "Phone")}
                      </p>
                      <p className="text-gray-300">+31 6 39182624</p>
                    </div>
                  </div>

                  <div className="flex items-start space-x-3">
                    <Mail className="h-5 w-5 text-primary mt-1 shrink-0" />
                    <div>
                      <p className="font-semibold text-white">
                        {t("1364", "Email")}
                      </p>
                      <p className="text-gray-300">
                        info@taxischipholairport.net
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Business Hours */}
              <div className="bg-gray-800/50 backdrop-blur-xs rounded-3xl p-6 sm:p-8 border border-gray-700">
                <div className="flex items-center space-x-3 mb-6">
                  <Clock className="h-8 w-8 text-primary" />
                  <h3 className="text-2xl font-bold text-white">
                    {t("1365", "Business Hours")}
                  </h3>
                </div>

                <div className="space-y-3">
                  <div className="flex justify-between items-center py-2 border-b border-gray-700">
                    <span className="text-gray-300">
                      {t("1366", "Monday - Sunday")}
                    </span>
                    <span className="text-primary font-semibold">
                      {t("1367", "24/7 Available")}
                    </span>
                  </div>
                  <div className="flex justify-between items-center py-2 border-b border-gray-700">
                    <span className="text-gray-300">
                      {t("1368", "Holidays")}
                    </span>
                    <span className="text-primary font-semibold">
                      {t("1369", "24/7 Available")}
                    </span>
                  </div>
                </div>

                <div className="mt-6 p-4 bg-primary/10 rounded-xl border border-primary/20">
                  <div className="flex items-center space-x-2 text-primary">
                    <CheckCircle className="h-5 w-5" />
                    <span className="font-semibold">
                      {t("1370", "Available 24/7 for your convenience")}
                    </span>
                  </div>
                </div>
              </div>

              {/* Reviews */}
              <div className="bg-gray-800/50 backdrop-blur-xs rounded-3xl p-6 sm:p-8 border border-gray-700">
                <div className="flex items-center space-x-3 mb-6">
                  <Star className="h-8 w-8 text-warning" />
                  <h3 className="text-2xl font-bold text-white">
                    {t("1371", "Customer Reviews")}
                  </h3>
                </div>

                <div className="space-y-4">
                  <div className="flex items-center space-x-2">
                    <div className="flex space-x-1">
                      {[...Array(5)].map((_, i) => (
                        <Star
                          key={i}
                          className="h-5 w-5 text-warning fill-current"
                        />
                      ))}
                    </div>
                    <span className="text-white font-semibold">
                      {t("1372", "5.0 Rating")}
                    </span>
                  </div>

                  <p className="text-gray-300 italic">
                    {t(
                      "1373",
                      '"Excellent service! Professional drivers and comfortable vehicles. Highly recommended for airport transfers."'
                    )}
                  </p>

                  <div className="mt-8 space-y-4">
                    <p className="text-center text-lg font-semibold text-gray-200">
                      {t("1716", "Leave us a review")}
                    </p>
                    <div className="flex items-center justify-center gap-6">
                      <a
                        href="https://g.page/r/CcSMCW_1ucbXEBI/review"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex flex-col items-center gap-2 p-4 rounded-lg bg-gray-800/50 hover:bg-gray-800 transition-colors group"
                      >
                        <img
                          src="/images/Google.webp"
                          alt={t("1375", "Google Reviews")}
                          className="h-12 w-auto"
                        />
                        <span className="text-sm text-gray-400 group-hover:text-primary transition-colors">
                          {t("1717", "Review on Google")}
                        </span>
                      </a>
                      <a
                        href="https://www.trustpilot.com/evaluate/taxischipholairport.net"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex flex-col items-center gap-2 p-4 rounded-lg bg-gray-800/50 hover:bg-gray-800 transition-colors group"
                      >
                        <img
                          src="/images/tru.webp"
                          alt={t("1376", "Trustpilot Reviews")}
                          className="h-12 w-auto"
                        />
                        <span className="text-sm text-gray-400 group-hover:text-primary transition-colors">
                          {t("1718", "Review on Trustpilot")}
                        </span>
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Map Section */}
      {/* <section id="map" className="py-16 sm:py-20 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-center mb-12"
          >
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Find Us at Schiphol
            </h2>
            <p className="text-xl text-gray-300">
              Located conveniently at Schiphol Airport for easy access
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="bg-gray-800/50 backdrop-blur-xs rounded-3xl p-6 sm:p-8 border border-gray-700"
          >
            <div className="w-full h-96 bg-gray-700 rounded-2xl flex items-center justify-center">
              <div className="text-center text-gray-400">
                <MapPin className="h-16 w-16 mx-auto mb-4" />
                <p className="text-lg font-semibold">Interactive Map</p>
                <p className="text-sm">
                  Stationsplein NO 264, 1046 AH Schiphol
                </p>
                <p className="text-xs mt-2">
                  Google Maps integration would be implemented here
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </section> */}
    </div>
  );
};
